/**
 * Global Kanban Component
 * 
 * A reusable kanban board component with drag-and-drop functionality.
 * Configure it through the config prop to match your specific use case.
 */

import { useState, useEffect, ReactNode, useMemo } from 'react';
import { KanbanIcon, PlusIcon, InfoIcon, Eye, EyeSlash, Minus, Plus, TextAa, ArrowsClockwise } from "@phosphor-icons/react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragStartEvent,
  DragOverlay,
  useDroppable,
  useDraggable,
} from '@dnd-kit/core';
import { KanbanProps, KanbanItem } from './types';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '@/components/ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';

/**
 * Droppable Column Component
 */
interface DroppableColumnProps {
  id: string;
  children: (isOver: boolean) => ReactNode;
}

const DroppableColumn = ({ id, children }: DroppableColumnProps) => {
  const { setNodeRef, isOver } = useDroppable({ id });
  return (
    <div ref={setNodeRef} className="flex-1 flex flex-col min-h-0">
      {children(isOver)}
    </div>
  );
};

/**
 * Load More Button Component
 */
interface LoadMoreButtonProps {
  hasMore: boolean;
  isLoadingMore: boolean;
  onLoadMore: () => void;
  loadMoreText?: string;
  loadingText?: string;
  color: string;
}

const LoadMoreButton = ({
  hasMore,
  isLoadingMore,
  onLoadMore,
  loadMoreText = 'Meer laden...',
  loadingText = 'Laden...',
  color
}: LoadMoreButtonProps) => {
  if (!hasMore) return null;

  return (
    <button
      onClick={(e) => {
        e.stopPropagation();
        onLoadMore();
      }}
      disabled={isLoadingMore}
      className={`w-full mt-3 px-4 py-2 text-sm font-medium rounded-md border-2 border-dashed transition-all ${
        isLoadingMore
          ? 'opacity-50 cursor-not-allowed bg-neutral-100 border-neutral-300'
          : `${color} border-current opacity-60 hover:opacity-100 hover:bg-white/50`
      }`}
    >
      {isLoadingMore ? (
        <div className="flex items-center justify-center gap-2">
          <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
          <span>{loadingText}</span>
        </div>
      ) : (
        loadMoreText
      )}
    </button>
  );
};

/**
 * Draggable Card Wrapper Component
 */
interface DraggableCardProps<TItem extends KanbanItem> {
  item: TItem;
  children: ReactNode;
  onCardClick?: (item: TItem) => void;
}

const DraggableCard = <TItem extends KanbanItem>({ item, children, onCardClick }: DraggableCardProps<TItem>) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    isDragging,
  } = useDraggable({ id: item.id });

  const style = {
    transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
    opacity: isDragging ? 0.5 : 1,
    // Performance optimizations for smooth dragging
    willChange: isDragging ? 'transform' : 'auto',
    transition: isDragging ? 'none' : 'opacity 150ms ease',
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className="w-full transform-gpu"
      onClick={() => onCardClick?.(item)}
    >
      {children}
    </div>
  );
};

/**
 * Main Kanban Component
 */
export function Kanban<TItem extends KanbanItem, TStatus = string>({
  config,
  items,
  renderCard,
  onDrop,
  onAddClick,
  onCardClick,
  onColumnHeaderClick,
  onFetchAllInColumn,
  loading = false,
  dragActivationDistance = 8,
  columnPagination = [],
  hiddenColumns = [],
  onToggleColumnVisibility,
  fontSize = 12,
  onIncreaseFontSize,
  onDecreaseFontSize,
  headerRow,
}: KanbanProps<TItem, TStatus>) {
  // Defensive guards to prevent runtime crashes if misconfigured
  if (!config || !Array.isArray((config as any).columns)) {
    return (
      <div className="flex flex-col items-center gap-2 py-12 text-neutral-500">
        <div className="w-8 h-8 border-4 border-neutral-200 border-t-neutral-500 rounded-full animate-spin" />
        <div className="text-sm">Kanban configuratie ontbreekt of is ongeldig</div>
      </div>
    );
  }

  // Ensure items is always an array
  const safeItems = Array.isArray(items) ? items : ([] as unknown as TItem[]);
  const [activeId, setActiveId] = useState<string | number | null>(null);
  const [localItems, setLocalItems] = useState<TItem[]>(safeItems);

  // Update local state when prop changes
  useEffect(() => {
    setLocalItems(safeItems);
  }, [safeItems]);

  // Drag and drop sensors - OPTIMIZED for better responsiveness
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: dragActivationDistance,
      },
    }),
    useSensor(KeyboardSensor)
  );

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id);
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    setActiveId(null);

    if (!over) {
      return;
    }

    const itemId = active.id;
    let newStatus: TStatus;

    // Check if dropped on another item (get its status)
    const overItem = localItems.find(item => item.id === over.id);
    if (overItem) {
      newStatus = config.getItemStatus(overItem);
    } else {
      // Dropped on a column
      newStatus = over.id as TStatus;
    }

    // Find the item being dragged
    const item = localItems.find(i => i.id === itemId);
    if (!item) {
      return;
    }

    const oldStatus = config.getItemStatus(item);

    // Always allow drop (even if status hasn't changed - user might want to reorder)
    // Only call onDrop if status has actually changed to avoid unnecessary API calls
    if (oldStatus !== newStatus) {
      // Call the onDrop handler (parent handles optimistic updates and API calls)
      await onDrop(itemId, newStatus, oldStatus);
    }
  };

  const handleDragCancel = () => {
    setActiveId(null);
  };

  // Get the active item for drag overlay
  const activeItem = activeId ? localItems.find(i => i.id === activeId) : null;

  // Helper function to get pagination config for a specific column
  const getPaginationForColumn = (columnId: TStatus) => {
    return columnPagination?.find(p => p.columnId === columnId);
  };

  // Default configuration values
  const {
    columns,
    getItemStatus,
    showCount = true,
    showAddButton = false,
    emptyState = {
      icon: <KanbanIcon size={24} weight="duotone" />,
      message: 'Geen items',
    },
    gridCols,
    scrollableColumns = false,
    maxHeight = 'max-h-[calc(100vh-var(--sticky-top,0px)-200px)]',
    tooltips = false,
  } = config;

  // MEMOIZE: Column items lookup - group items by status once instead of filtering per column
  const itemsByStatus = useMemo(() => {
    const grouped = new Map<TStatus, TItem[]>();

    // Initialize all columns with empty arrays
    columns.forEach(column => {
      grouped.set(column.id, []);
    });

    // Group items by their status
    localItems.forEach(item => {
      const status = getItemStatus(item);
      const items = grouped.get(status);
      if (items) {
        items.push(item);
      }
    });

    return grouped;
  }, [localItems, getItemStatus, columns]);

  // Filter visible columns
  const visibleColumns = useMemo(() => {
    return columns.filter(col => !hiddenColumns.includes(col.id));
  }, [columns, hiddenColumns]);

  // Count hidden columns
  const hiddenCount = columns.length - visibleColumns.length;

  // Auto-generate grid columns based on number of VISIBLE columns if not provided
  const visibleColumnCountClass = useMemo(() => {
    const map: Record<number, string> = {
      1: 'xl:grid-cols-1',
      2: 'xl:grid-cols-2',
      3: 'xl:grid-cols-3',
      4: 'xl:grid-cols-4',
      5: 'xl:grid-cols-5',
      6: 'xl:grid-cols-6',
      7: 'xl:grid-cols-7',
      8: 'xl:grid-cols-8',
      9: 'xl:grid-cols-9',
      10: 'xl:grid-cols-10',
      11: 'xl:grid-cols-11',
      12: 'xl:grid-cols-12',
    };
    return map[visibleColumns.length] || 'xl:grid-cols-5';
  }, [visibleColumns.length]);

  const autoGridCols = gridCols || `grid-cols-1 md:grid-cols-2 lg:grid-cols-3 ${visibleColumnCountClass}`;

  if (loading) {
    return (
      <div className="flex flex-col items-center gap-4 py-12">
        <div className="w-8 h-8 border-4 border-moss-primary/20 border-t-moss-primary rounded-full animate-spin"></div>
        <div className="text-center">
          <p className="text-lg font-medium text-neutral-primary">Aan het laden...</p>
          <p className="text-sm text-neutral-600">Even geduld terwijl we je gegevens ophalen...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      {/* Font Size and Column Visibility Controls */}
      {(onToggleColumnVisibility || (onIncreaseFontSize && onDecreaseFontSize)) && (
        <div className="mb-4 flex justify-end gap-3">
          {/* Font Size Controls */}
          {onIncreaseFontSize && onDecreaseFontSize && (
            <div className="flex items-center gap-1 rounded-lg border border-input bg-background p-1 shadow-sm">
              <Button
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0"
                onClick={onDecreaseFontSize}
                disabled={fontSize <= 10}
                title="Kleiner lettertype"
              >
                <Minus size={14} weight="bold" />
              </Button>

              <div className="flex items-center gap-1 px-2">
                <TextAa size={16} weight="regular" className="text-neutral-600" />
                <span className="text-xs font-medium text-neutral-600">
                  {fontSize}px
                </span>
              </div>

              <Button
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0"
                onClick={onIncreaseFontSize}
                disabled={fontSize >= 16}
                title="Groter lettertype"
              >
                <Plus size={14} weight="bold" />
              </Button>
            </div>
          )}

          {/* Column Visibility Toggle */}
          {onToggleColumnVisibility && (
            <DropdownMenu modal={false}>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="gap-2">
                  <Eye size={16} weight="regular" />
                  <span>Kolommen ({visibleColumns.length}/{columns.length})</span>
                  {hiddenCount > 0 && (
                    <span className="ml-1 rounded-full bg-moss-primary/10 px-2 py-0.5 text-xs font-medium text-moss-primary">
                      {hiddenCount} verborgen
                    </span>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56" sideOffset={5}>
                <DropdownMenuLabel>Toon/verberg kolommen</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {columns.map((column) => {
                  const isVisible = !hiddenColumns.includes(column.id);
                  return (
                    <DropdownMenuCheckboxItem
                      key={String(column.id)}
                      checked={isVisible}
                      onCheckedChange={() => onToggleColumnVisibility(column.id)}
                      onSelect={(event) => {
                        // Prevent dropdown from closing when toggling checkboxes
                        event.preventDefault();
                      }}
                    >
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                          {isVisible ? (
                            <Eye size={14} weight="fill" className="text-moss-primary" />
                          ) : (
                            <EyeSlash size={14} weight="regular" className="text-neutral-400" />
                          )}
                        </div>
                        <span className={isVisible ? '' : 'text-neutral-400'}>{column.label}</span>
                      </div>
                    </DropdownMenuCheckboxItem>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      )}

      <div className={`${activeId ? 'overflow-hidden' : 'overflow-x-auto'} pb-3`} style={{ scrollbarGutter: 'stable' }}>
      {headerRow && headerRow.length > 0 && (
        <div className={`grid ${autoGridCols} gap-3 mb-2`}>
          {headerRow}
        </div>
      )}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
      >
        <div className={`grid ${autoGridCols} gap-3`}>
            {visibleColumns.map((column) => {
              // Get items from memoized lookup (O(1) instead of O(n) filter)
              const columnItems = itemsByStatus.get(column.id) || [];

              return (
                <div
                  key={String(column.id)}
                  className={`flex flex-col gap-2 h-full ${
                    scrollableColumns ? `${maxHeight} min-h-0` : ''
                  }`}
                  style={{ minWidth: 0, overflowX: 'hidden' }}
                >
                  {/* Column Header - Pill Style */}
                  <div
                    className={`rounded-full ${column.headerBgColor} ${
                      scrollableColumns ? 'sticky top-0 z-10 shadow-sm' : ''
                    } px-4 py-2.5 shrink-0 ${onColumnHeaderClick ? 'cursor-pointer hover:opacity-90 transition-opacity' : ''}`}
                    onClick={() => onColumnHeaderClick?.(column.id)}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <h4
                              className={`text-sm font-medium ${column.color} line-clamp-2 ${onColumnHeaderClick ? 'cursor-pointer' : 'cursor-help'}`}
                            >
                              {column.label}
                            </h4>
                          </TooltipTrigger>
                          <TooltipContent side="top" className="max-w-xs">
                            <p className="text-center">{onColumnHeaderClick ? `Klik om te filteren op ${column.label}` : column.label}</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                      <div className="flex items-center gap-2">
                        {showAddButton && onAddClick && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              onAddClick(column.id);
                            }}
                            className={`flex items-center justify-center w-5 h-5 rounded-full hover:bg-white/80 transition-colors ${column.color} opacity-60 hover:opacity-100`}
                            title={`Item toevoegen aan ${column.label}`}
                          >
                            <PlusIcon size={14} weight="bold" />
                          </button>
                        )}
                        {(showCount || (tooltips && column.tooltip)) && (
                          <div className="flex items-center gap-1">
                            {showCount && (
                              <div
                                className={`flex items-center justify-center min-w-[22px] h-5 px-1.5 rounded-full bg-white ${column.color} text-xs font-semibold shadow-sm`}
                              >
                                {columnItems.length}
                              </div>
                            )}
                            {onFetchAllInColumn && (
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        onFetchAllInColumn(column.id);
                                      }}
                                      className={`flex items-center justify-center w-5 h-5 rounded-full hover:bg-white/80 transition-colors ${column.color} opacity-60 hover:opacity-100`}
                                      title={`Alle tickets in ${column.label} ophalen`}
                                    >
                                      <ArrowsClockwise size={14} weight="bold" />
                                    </button>
                                  </TooltipTrigger>
                                  <TooltipContent side="top">
                                    <p className="text-center">Alle tickets in deze fase ophalen</p>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            )}
                            {tooltips && column.tooltip ? (
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <button
                                      type="button"
                                      className={`${column.color} opacity-60 hover:opacity-100 transition-opacity focus:outline-none`}
                                      aria-label={`Meer info over ${column.label}`}
                                      onClick={(event) => event.stopPropagation()}
                                    >
                                      <InfoIcon size={16} weight="bold" />
                                    </button>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p className="max-w-xs text-center">{column.tooltip}</p>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            ) : null}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Droppable Column */}
                  {scrollableColumns ? (
                    <DroppableColumn id={String(column.id)}>
                      {(isOver: boolean) => {
                        const pagination = getPaginationForColumn(column.id);
                        return (
                          <div
                            className={`flex-1 min-h-0 rounded-lg border-2 border-dashed overflow-y-auto overflow-x-hidden transition-colors ${
                              isOver
                                ? `${column.borderColor} ${column.bgColor} border-solid`
                                : 'border-neutral-200 bg-neutral-50/30'
                            }`}
                            style={{ overscrollBehavior: 'contain' }}
                          >
                            <div className="p-4">
                              <div className="space-y-3">
                                {columnItems.map((item) => (
                                  <DraggableCard<TItem>
                                    key={item.id}
                                    item={item}
                                    onCardClick={onCardClick}
                                  >
                                    {renderCard(item)}
                                  </DraggableCard>
                                ))}
                                {columnItems.length === 0 && (
                                  <div className="flex flex-col items-center justify-center py-12 text-neutral-400">
                                    {emptyState.icon}
                                    <p className="text-xs mt-2">
                                      {emptyState.message} in {column.label}
                                    </p>
                                  </div>
                                )}
                              </div>

                              {/* Load More Button */}
                              {pagination && (
                                <LoadMoreButton
                                  hasMore={pagination.hasMore}
                                  isLoadingMore={pagination.isLoadingMore}
                                  onLoadMore={pagination.onLoadMore}
                                  loadMoreText={pagination.loadMoreText}
                                  loadingText={pagination.loadingText}
                                  color={column.color}
                                />
                              )}
                            </div>
                          </div>
                        );
                      }}
                    </DroppableColumn>
                  ) : (
                    <DroppableColumn id={String(column.id)}>
                      {(isOver: boolean) => {
                        const pagination = getPaginationForColumn(column.id);
                        return (
                          <div
                            className={`flex-1 rounded-lg border-2 border-dashed p-4 h-full transition-colors ${
                              isOver
                                ? `${column.borderColor} ${column.bgColor} border-solid`
                                : 'border-neutral-200 bg-neutral-50/30'
                            }`}
                            style={{ overflowX: 'hidden' }}
                          >
                            <div className="space-y-5" style={{ overflowX: 'hidden' }}>
                              {columnItems.map((item) => (
                                <DraggableCard<TItem>
                                  key={item.id}
                                  item={item}
                                  onCardClick={onCardClick}
                                >
                                  {renderCard(item)}
                                </DraggableCard>
                              ))}
                              {columnItems.length === 0 && (
                                <div className="flex flex-col items-center justify-center py-12 text-neutral-400">
                                  {emptyState.icon}
                                  <p className="text-xs mt-2">
                                    {emptyState.message} in {column.label}
                                  </p>
                                </div>
                              )}
                            </div>

                            {/* Load More Button */}
                            {pagination && (
                              <LoadMoreButton
                                hasMore={pagination.hasMore}
                                isLoadingMore={pagination.isLoadingMore}
                                onLoadMore={pagination.onLoadMore}
                                loadMoreText={pagination.loadMoreText}
                                loadingText={pagination.loadingText}
                                color={column.color}
                              />
                            )}
                          </div>
                        );
                      }}
                    </DroppableColumn>
                  )}
                </div>
              );
            })}
          </div>

          {/* Drag Overlay */}
          <DragOverlay>
            {activeItem ? (
              <div className="opacity-90 shadow-2xl rounded-2xl ring-2 ring-moss-primary">
                {renderCard(activeItem)}
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>
      </div>
    </div>
  );
}

